import React, { useState } from 'react';
import { Code2, ExternalLink, Play } from 'lucide-react';
import StackBlitzEmbed from '../components/StackBlitzEmbed';

interface Example {
  id: string;
  title: string;
  description: string;
  framework: 'react' | 'vue' | 'vanilla';
  stackblitzId: string;
  tags: string[];
  code: string;
}

const ExamplesPage: React.FC = () => {
  const [selectedExample, setSelectedExample] = useState<Example | null>(null);

  const examples: Example[] = [
    {
      id: 'react-basic',
      title: 'React Basic Form',
      description: 'Simple address form with validation using the latest useAddressForm hook.',
      framework: 'react',
      stackblitzId: 'salla-address-react-basic',
      tags: ['React', 'TypeScript', 'v3.0'],
      code: `import { useAddressForm } from 'salla-address-manager/react';

function AddressForm() {
  const form = useAddressForm({
    locale: 'en',
    validateOnChange: true, // Real-time validation
    validateOnBlur: true,   // Validate on field blur
    validationDelay: 300    // Debounce delay
  });

  const handleSubmit = form.handleSubmit(
    async (data) => {
      // Valid data
      await fetch('/api/addresses', {
        method: 'POST',
        body: JSON.stringify(data)
      });
      alert('Address saved!');
    },
    (errors) => {
      // Validation errors
      console.error('Errors:', errors);
    }
  );

  return (
    <form onSubmit={handleSubmit}>
      {/* Country */}
      <div>
        <label>Country *</label>
        <select {...form.getFieldProps('country')}>
          <option value="">Select Country</option>
          {form.countries.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        {form.errors.country && (
          <span className="error">{form.errors.country}</span>
        )}
      </div>

      {/* City (auto-disabled until country is selected) */}
      <div>
        <label>City *</label>
        <select {...form.getFieldProps('city')}>
          <option value="">
            {form.loading.cities ? 'Loading...' : 'Select City'}
          </option>
          {form.currentCities.map(city => (
            <option key={city.id} value={city.id}>{city.name}</option>
          ))}
        </select>
        {form.errors.city && (
          <span className="error">{form.errors.city}</span>
        )}
      </div>

      {/* District (auto-disabled until city is selected) */}
      <div>
        <label>District</label>
        <select {...form.getFieldProps('district')}>
          <option value="">Select District</option>
          {form.currentDistricts.map(district => (
            <option key={district.id} value={district.id}>
              {district.name}
            </option>
          ))}
        </select>
      </div>

      {/* Street */}
      <div>
        <label>Street *</label>
        <input {...form.getFieldProps('street')} />
        {form.errors.street && (
          <span className="error">{form.errors.street}</span>
        )}
      </div>

      <button 
        type="submit" 
        disabled={!form.state.isValid || form.state.isSubmitting}
      >
        {form.state.isSubmitting ? 'Saving...' : 'Save Address'}
      </button>
    </form>
  );
}`,
    },
    {
      id: 'react-async',
      title: 'React with Salla API',
      description: 'Complete integration with Salla API using addressToFormDefaults and toSallaPayload helpers.',
      framework: 'react',
      stackblitzId: 'salla-address-react-api',
      tags: ['React', 'Salla API', 'Helpers', 'v3.0'],
      code: `import { useAddressForm } from 'salla-address-manager/react';
import { 
  addressToFormDefaults, 
  toSallaPayload 
} from 'salla-address-manager/helpers';

function SallaAddressForm({ existingAddress }) {
  const form = useAddressForm({
    locale: 'ar',
    validateOnChange: true,
    
    // 🔥 Async options API - fetch from Salla API
    countryOptions: {
      queryFn: () => fetch('/api/countries').then(r => r.json()),
      transform: (res) => res.data.data
    },
    cityOptions: {
      queryFn: (countryId) => 
        fetch(\`/api/countries/\${countryId}/cities\`)
          .then(r => r.json()),
      transform: (res) => res.data.data
    },
    districtOptions: {
      queryFn: (cityId) => 
        fetch(\`/api/cities/\${cityId}/districts\`)
          .then(r => r.json()),
      transform: (res) => res.data.data
    },
    
    // 🔥 Transform existing address to form defaults
    defaultValues: existingAddress 
      ? addressToFormDefaults(existingAddress, {
          includeDistrictId: true,
          customMappings: {
            area: (addr) => addr.local || ''
          }
        }) 
      : undefined
  });

  const handleSubmit = form.handleSubmit(async (formData) => {
    // Step 1: Transform to base payload
    const basePayload = form.toAPIPayload({
      fieldMappings: {
        country: 'country_id',
        city: 'city_id',
        district: 'district_id'
      },
      includeNames: ['district'],
      defaults: {
        district_id: 0, // Required by Salla API
        lat: 24.7136,
        lng: 46.6753
      }
    });

    // Step 2: Transform to Salla API format
    const requestPayload = toSallaPayload(basePayload, {
      soft: true,
      type: 'location_id'
    });

    // Step 3: Send to Salla API
    await fetch('/api/addresses', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestPayload)
    });
    
    alert('تم حفظ العنوان بنجاح!');
  });

  return (
    <form onSubmit={handleSubmit} dir="rtl">
      <div>
        <label>الدولة *</label>
        <select {...form.getFieldProps('country')}>
          <option value="">اختر الدولة</option>
          {form.countries.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        {form.errors.country && <span>{form.errors.country}</span>}
      </div>

      <div>
        <label>المدينة *</label>
        <select {...form.getFieldProps('city')}>
          <option value="">اختر المدينة</option>
          {form.currentCities.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        {form.errors.city && <span>{form.errors.city}</span>}
      </div>

      <button type="submit" disabled={!form.state.isValid}>
        حفظ العنوان
      </button>
    </form>
  );
}`,
    },
    {
      id: 'react-multi-language',
      title: 'Multi-Language Support',
      description: 'Dynamic language switching between English and Arabic with RTL support.',
      framework: 'react',
      stackblitzId: 'salla-address-react-i18n',
      tags: ['React', 'i18n', 'RTL', 'v3.0'],
      code: `import { useState } from 'react';
import { useAddressForm } from 'salla-address-manager/react';

function MultiLanguageForm() {
  const [locale, setLocale] = useState<'en' | 'ar'>('en');
  
  const form = useAddressForm({
    locale, // Dynamic locale
    validateOnChange: true,
    validateOnBlur: true
  });

  const isRTL = locale === 'ar';

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Language Switcher */}
      <button onClick={() => setLocale(locale === 'en' ? 'ar' : 'en')}>
        {locale === 'en' ? 'العربية' : 'English'}
      </button>

      <form onSubmit={form.handleSubmit(async (data) => {
        alert(locale === 'ar' 
          ? 'تم حفظ العنوان!' 
          : 'Address saved!');
      })}>
        <div>
          <label>
            {locale === 'ar' ? 'الدولة' : 'Country'} *
          </label>
          <select {...form.getFieldProps('country')}>
            {form.countries.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          {/* Errors auto-localize! */}
          {form.errors.country && <span>{form.errors.country}</span>}
        </div>
        
        <button type="submit" disabled={!form.isValid}>
          {locale === 'ar' ? 'حفظ' : 'Save'}
        </button>
      </form>
    </div>
  );
}`,
    },
    {
      id: 'react-national-address',
      title: 'Saudi National Address',
      description: 'Autocomplete for Saudi national addresses with auto-population.',
      framework: 'react',
      stackblitzId: 'salla-address-react-national',
      tags: ['React', 'Saudi Arabia', 'Autocomplete', 'v3.0'],
      code: `import { useAddressForm, useNationalAddress } from 'salla-address-manager/react';

function NationalAddressForm() {
  const form = useAddressForm({
    locale: 'ar',
    enableNationalAddress: true,
    defaultValues: { country: '1473353380' } // Saudi Arabia
  });

  // 🔥 NEW: useNationalAddress hook
  const nationalAddress = useNationalAddress({
    form, // Auto-populate form
    queryFn: ({ address, customer_id }) =>
      fetch('/api/national-address/search', {
        method: 'POST',
        body: JSON.stringify({ address, customer_id })
      }).then(r => r.json()),
    transform: (res) => res.data.data,
    debounceMs: 400,
    customerId: 123,
    onSelect: (formData, item) => {
      console.log('Auto-populated:', formData);
    }
  });

  return (
    <form onSubmit={form.handleSubmit}>
      {/* National Address Autocomplete */}
      <div>
        <label>العنوان الوطني</label>
        <input 
          {...nationalAddress.getInputProps()} 
          placeholder="AAAA1234"
          maxLength={8}
        />
        {nationalAddress.isLoading && <span>جاري البحث...</span>}
        
        {/* Autocomplete Results */}
        {nationalAddress.results.length > 0 && (
          <div className="autocomplete-dropdown">
            {nationalAddress.results.map(item => (
              <button
                key={item.value}
                onClick={() => nationalAddress.selectResult(item)}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Auto-populated fields */}
      <input {...form.getFieldProps('building_number')} />
      <input {...form.getFieldProps('postal_code')} />
      <input {...form.getFieldProps('street')} />
    </form>
  );
}`,
    },
    {
      id: 'vue3-composition',
      title: 'Vue 3 Composition API',
      description: 'Reactive address form using Vue 3 Composition API with useAddressForm and getFieldProps.',
      framework: 'vue',
      stackblitzId: 'salla-address-vue3-composition',
      tags: ['Vue 3', 'Composition API', 'v3.0'],
      code: `<script setup lang="ts">
import { useAddressForm } from 'salla-address-manager/vue3';

const form = useAddressForm({
  locale: 'en',
  validateOnChange: true,
  validateOnBlur: true,
  validationDelay: 300
});

// Use handleSubmit for easy form submission
const handleSubmit = form.handleSubmit(
  async (data) => {
    // Transform data to API format
    const payload = form.toAPIPayload({
      fieldMappings: {
        country: 'country_id',
        city: 'city_id',
        district: 'district_id'
      },
      includeNames: ['district'], // Include district name
      defaults: {
        type: 'delivery',
        lat: 24.7136,
        lng: 46.6753
      }
    });
    
    const response = await fetch('/api/addresses', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    
    if (response.ok) {
      alert('Address saved!');
      form.resetForm();
    }
  },
  (errors) => {
    console.error('Validation errors:', errors);
  }
);
</script>

<template>
  <form @submit.prevent="handleSubmit">
    <!-- Using getFieldProps for automatic configuration -->
    <div>
      <label>Country *</label>
      <select v-bind="form.getFieldProps('country')">
        <option value="">Select Country</option>
        <option 
          v-for="country in form.countries.value" 
          :key="country.id" 
          :value="country.id"
        >
          {{ country.name }}
        </option>
      </select>
      <span v-if="form.errors.value.country" class="error">
        {{ form.errors.value.country }}
      </span>
    </div>

    <!-- City (auto-loaded when country changes) -->
    <div>
      <label>City *</label>
      <select v-bind="form.getFieldProps('city')">
        <option value="">
          {{ form.loading.value.cities ? 'Loading...' : 'Select City' }}
        </option>
        <option 
          v-for="city in form.currentCities.value" 
          :key="city.id" 
          :value="city.id"
        >
          {{ city.name }}
        </option>
      </select>
      <span v-if="form.errors.value.city" class="error">
        {{ form.errors.value.city }}
      </span>
    </div>

    <!-- District (auto-loaded when city changes) -->
    <div>
      <label>District</label>
      <select v-bind="form.getFieldProps('district')">
        <option value="">Select District</option>
        <option 
          v-for="district in form.currentDistricts.value" 
          :key="district.id" 
          :value="district.id"
        >
          {{ district.name }}
        </option>
      </select>
    </div>

    <!-- Street -->
    <div>
      <label>Street *</label>
      <input v-bind="form.getFieldProps('street')" type="text" />
      <span v-if="form.errors.value.street" class="error">
        {{ form.errors.value.street }}
      </span>
    </div>

    <button 
      type="submit" 
      :disabled="!form.meta.value.isValid || form.meta.value.isSubmitting"
    >
      {{ form.meta.value.isSubmitting ? 'Saving...' : 'Save Address' }}
    </button>
  </form>
</template>`,
    },
    {
      id: 'vue3-national-address',
      title: 'Vue 3 National Address',
      description: 'Auto-populate form from Saudi National Address using Vue 3 Composition API.',
      framework: 'vue',
      stackblitzId: 'salla-address-vue3-national',
      tags: ['Vue 3', 'National Address', 'Autocomplete', 'v3.0'],
      code: `<script setup lang="ts">
import { useAddressForm, useNationalAddress } from 'salla-address-manager/vue3';

const form = useAddressForm({
  locale: 'ar',
  validateOnChange: true
});

// National address autocomplete
const nationalAddress = useNationalAddress({
  form, // Auto-populate form on selection
  queryFn: async ({ address, customer_id }) => {
    const response = await fetch('/api/national-addresses', {
      method: 'GET',
      params: { address, customer_id }
    });
    return response.json();
  },
  transform: (res) => res.data.data,
  customerId: 123,
  debounceMs: 400,
  getErrorMessage: (error) => {
    if (error.response?.status === 404) {
      return 'لم يتم العثور على عناوين';
    }
    return 'فشل في جلب العناوين';
  },
  onSelect: (formData, item) => {
    console.log('Selected address:', formData);
  }
});

const handleSubmit = form.handleSubmit(
  async (data) => {
    const payload = form.toAPIPayload({
      fieldMappings: {
        country: 'country_id',
        city: 'city_id'
      }
    });
    
    await fetch('/api/addresses', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    
    alert('تم حفظ العنوان!');
  }
);
</script>

<template>
  <div>
    <!-- National Address Search -->
    <div class="national-address-search">
      <label>ابحث عن العنوان الوطني</label>
      <input
        v-model="nationalAddress.searchValue.value"
        type="text"
        placeholder="ابحث بالعنوان الوطني..."
        :disabled="nationalAddress.isLoading.value"
      />
      
      <!-- Autocomplete Results -->
      <ul v-if="nationalAddress.items.value.length" class="autocomplete-list">
        <li 
          v-for="item in nationalAddress.items.value"
          :key="item.value"
          @click="nationalAddress.handleSelect(item.value)"
          class="autocomplete-item"
        >
          {{ item.label }}
        </li>
      </ul>
      
      <div v-if="nationalAddress.isLoading.value" class="loading">
        جاري البحث...
      </div>
    </div>

    <!-- Address Form (auto-populated from national address) -->
    <form @submit.prevent="handleSubmit">
      <div>
        <label>المدينة *</label>
        <select v-bind="form.getFieldProps('city')">
          <option value="">اختر المدينة</option>
          <option 
            v-for="city in form.currentCities.value" 
            :key="city.id" 
            :value="city.id"
          >
            {{ city.name }}
          </option>
        </select>
      </div>

      <div>
        <label>الحي</label>
        <select v-bind="form.getFieldProps('district')">
          <option value="">اختر الحي</option>
          <option 
            v-for="district in form.currentDistricts.value" 
            :key="district.id" 
            :value="district.id"
          >
            {{ district.name }}
          </option>
        </select>
      </div>

      <div>
        <label>الشارع *</label>
        <input v-bind="form.getFieldProps('street')" />
      </div>

      <button type="submit" :disabled="!form.meta.value.isValid">
        حفظ العنوان
      </button>
    </form>
  </div>
</template>`,
    },
    {
      id: 'vue2-basic',
      title: 'Vue 2 Options API',
      description: 'Address form using Vue 2 with AddressSystemMixin and getFieldProps.',
      framework: 'vue',
      stackblitzId: 'salla-address-vue2-basic',
      tags: ['Vue 2', 'Options API', 'Mixins', 'v3.0'],
      code: `<template>
  <form @submit.prevent="handleSubmit">
    <!-- Using getFieldProps for automatic configuration -->
    <div>
      <label>Country *</label>
      <select v-bind="getFieldProps('country')">
        <option value="">Select Country</option>
        <option 
          v-for="country in countries" 
          :key="country.id" 
          :value="country.id"
        >
          {{ country.name }}
        </option>
      </select>
      <span v-if="errors.country" class="error">
        {{ errors.country }}
      </span>
    </div>

    <!-- City (auto-loaded when country changes) -->
    <div>
      <label>City *</label>
      <select v-bind="getFieldProps('city')">
        <option value="">
          {{ loading.cities ? 'Loading...' : 'Select City' }}
        </option>
        <option 
          v-for="city in currentCities" 
          :key="city.id" 
          :value="city.id"
        >
          {{ city.name }}
        </option>
      </select>
      <span v-if="errors.city" class="error">
        {{ errors.city }}
      </span>
    </div>

    <!-- District -->
    <div>
      <label>District</label>
      <select v-bind="getFieldProps('district')">
        <option value="">Select District</option>
        <option 
          v-for="district in currentDistricts" 
          :key="district.id" 
          :value="district.id"
        >
          {{ district.name }}
        </option>
      </select>
    </div>

    <!-- Street -->
    <div>
      <label>Street *</label>
      <input v-bind="getFieldProps('street')" type="text" />
      <span v-if="errors.street" class="error">
        {{ errors.street }}
      </span>
    </div>

    <button 
      type="submit" 
      :disabled="!meta.isValid || meta.isSubmitting"
    >
      {{ meta.isSubmitting ? 'Saving...' : 'Save Address' }}
    </button>
  </form>
</template>

<script>
import { AddressSystemMixin } from 'salla-address-manager/vue2';

export default {
  name: 'AddressForm',
  mixins: [AddressSystemMixin],
  
  created() {
    this.initializeAddressSystem({
      locale: 'en',
      validateOnChange: true,
      validateOnBlur: true,
      validationDelay: 300
    });
  },
  
  methods: {
    async handleSubmit() {
      // Use handleSubmit for form validation
      const submitHandler = this.handleSubmit(
        async (data) => {
          // Transform data to API format
          const payload = this.toAPIPayload({
            fieldMappings: {
              country: 'country_id',
              city: 'city_id',
              district: 'district_id'
            },
            includeNames: ['district'],
            defaults: {
              type: 'delivery',
              lat: 24.7136,
              lng: 46.6753
            }
          });
          
          const response = await fetch('/api/addresses', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
          
          if (response.ok) {
            alert('Address saved!');
            this.reset();
          }
        },
        (errors) => {
          console.error('Validation errors:', errors);
        }
      );
      
      await submitHandler();
    }
  }
};
</script>`,
    },
    {
      id: 'vue2-national-address',
      title: 'Vue 2 National Address',
      description: 'Auto-populate form from Saudi National Address using Vue 2 mixins.',
      framework: 'vue',
      stackblitzId: 'salla-address-vue2-national',
      tags: ['Vue 2', 'National Address', 'Mixins', 'v3.0'],
      code: `<template>
  <div>
    <!-- National Address Search -->
    <div class="national-address-search">
      <label>ابحث عن العنوان الوطني</label>
      <input
        v-model="nationalSearchValue"
        type="text"
        placeholder="ابحث بالعنوان الوطني..."
        :disabled="isNationalAddressLoading"
      />
      
      <!-- Autocomplete Results -->
      <ul v-if="nationalItems.length" class="autocomplete-list">
        <li 
          v-for="item in nationalItems"
          :key="item.value"
          @click="selectNationalAddress(item.value)"
          class="autocomplete-item"
        >
          {{ item.label }}
        </li>
      </ul>
      
      <div v-if="isNationalAddressLoading" class="loading">
        جاري البحث...
      </div>
    </div>

    <!-- Address Form (auto-populated from national address) -->
    <form @submit.prevent="handleSubmit">
      <div>
        <label>المدينة *</label>
        <select v-bind="getFieldProps('city')">
          <option value="">اختر المدينة</option>
          <option 
            v-for="city in currentCities" 
            :key="city.id" 
            :value="city.id"
          >
            {{ city.name }}
          </option>
        </select>
      </div>

      <div>
        <label>الحي</label>
        <select v-bind="getFieldProps('district')">
          <option value="">اختر الحي</option>
          <option 
            v-for="district in currentDistricts" 
            :key="district.id" 
            :value="district.id"
          >
            {{ district.name }}
          </option>
        </select>
      </div>

      <div>
        <label>الشارع *</label>
        <input v-bind="getFieldProps('street')" />
      </div>

      <button type="submit" :disabled="!meta.isValid">
        حفظ العنوان
      </button>
    </form>
  </div>
</template>

<script>
import { 
  AddressSystemMixin, 
  NationalAddressMixin 
} from 'salla-address-manager/vue2';

export default {
  name: 'NationalAddressForm',
  mixins: [AddressSystemMixin, NationalAddressMixin],
  
  created() {
    // Initialize address system
    this.initializeAddressSystem({
      locale: 'ar',
      validateOnChange: true
    });
    
    // Initialize national address
    this.initializeNationalAddress({
      queryFn: this.fetchNationalAddresses,
      transform: (res) => res.data.data,
      customerId: 123,
      debounceMs: 400,
      getErrorMessage: (error) => {
        if (error.response?.status === 404) {
          return 'لم يتم العثور على عناوين';
        }
        return 'فشل في جلب العناوين';
      },
      onSelect: (formData, item) => {
        console.log('Selected address:', formData);
      }
    });
  },
  
  methods: {
    async fetchNationalAddresses({ address, customer_id }) {
      const response = await fetch('/api/national-addresses', {
        method: 'GET',
        params: { address, customer_id }
      });
      return response.json();
    },
    
    async handleSubmit() {
      const submitHandler = this.handleSubmit(
        async (data) => {
          const payload = this.toAPIPayload({
            fieldMappings: {
              country: 'country_id',
              city: 'city_id'
            }
          });
          
          await fetch('/api/addresses', {
            method: 'POST',
            body: JSON.stringify(payload)
          });
          
          alert('تم حفظ العنوان!');
        }
      );
      
      await submitHandler();
    }
  }
};
</script>`,
    },
    {
      id: 'vanilla-complete',
      title: 'Vanilla JavaScript',
      description: 'Full-featured address form using pure JavaScript with createAddressClient.',
      framework: 'vanilla',
      stackblitzId: 'salla-address-vanilla-complete',
      tags: ['Vanilla JS', 'ES6', 'No Framework', 'v3.0'],
      code: `import { createAddressClient } from 'salla-address-manager';

// Create the address client
const client = createAddressClient({
  locale: 'en',
  validateOnChange: true,
  validateOnBlur: true,
  validationDelay: 300
});

// Get DOM elements
const form = document.getElementById('addressForm');
const countrySelect = document.getElementById('country');
const citySelect = document.getElementById('city');
const streetInput = document.getElementById('street');
const submitBtn = document.getElementById('submitBtn');

// Load countries
async function init() {
  const countries = await client.countries;
  countries.forEach(country => {
    const option = document.createElement('option');
    option.value = country.id;
    option.textContent = country.name;
    countrySelect.appendChild(option);
  });
}

// Update UI from state
function updateUI() {
  // Update submit button
  submitBtn.disabled = !client.state.isValid || client.state.isSubmitting;
  submitBtn.textContent = client.state.isSubmitting ? 'Saving...' : 'Save Address';
  
  // Update error messages
  Object.keys(client.state.errors).forEach(field => {
    const errorEl = document.getElementById(\`\${field}-error\`);
    if (errorEl) {
      errorEl.textContent = client.state.errors[field] || '';
    }
  });
}

// Event listeners
countrySelect.addEventListener('change', async (e) => {
  client.setValue('country', e.target.value);
  
  if (e.target.value) {
    // Load cities for selected country
    const cities = await client.getCities(e.target.value);
    citySelect.innerHTML = '<option value="">Select City</option>';
    cities.forEach(city => {
      const option = document.createElement('option');
      option.value = city.id;
      option.textContent = city.name;
      citySelect.appendChild(option);
    });
    citySelect.disabled = false;
  } else {
    citySelect.innerHTML = '<option value="">Select City</option>';
    citySelect.disabled = true;
  }
});

citySelect.addEventListener('change', (e) => {
  client.setValue('city', e.target.value);
});

streetInput.addEventListener('input', (e) => {
  client.setValue('street', e.target.value);
});

// Form submission
form.addEventListener('submit', async (e) => {
  e.preventDefault();
  
  const isValid = await client.validateForm();
  if (isValid) {
    // Submit to API
    await fetch('/api/addresses', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(client.data)
    });
    
    alert('Address saved!');
    form.reset();
  }
});

// Listen to state changes
client.on('form:change', updateUI);

// Initialize
init();`,
    },
  ];

  const frameworkColors = {
    react: 'bg-blue-100 text-blue-800',
    vue: 'bg-green-100 text-green-800',
    vanilla: 'bg-yellow-100 text-yellow-800',
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="container-custom py-12">
          <h1 className="text-4xl font-bold mb-4">Interactive Examples</h1>
          <p className="text-xl text-gray-600 max-w-3xl">
            Explore live examples of Salla Address Manager in action. Try them out
            directly in your browser with StackBlitz.
          </p>
        </div>
      </div>

      <div className="container-custom section">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Examples List */}
          <div className="lg:col-span-1 space-y-4">
            {examples.map((example) => (
              <div
                key={example.id}
                onClick={() => setSelectedExample(example)}
                className={`card cursor-pointer transition-all hover:shadow-md ${
                  selectedExample?.id === example.id
                    ? 'ring-2 ring-salla-primary'
                    : ''
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-bold text-lg">{example.title}</h3>
                  <span
                    className={`text-xs px-2 py-1 rounded ${
                      frameworkColors[example.framework]
                    }`}
                  >
                    {example.framework}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-3">{example.description}</p>
                <div className="flex flex-wrap gap-2">
                  {example.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Example Display */}
          <div className="lg:col-span-2">
            {selectedExample ? (
              <div className="space-y-6">
                {/* Example Header */}
                <div className="card">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-2xl font-bold mb-2">
                        {selectedExample.title}
                      </h2>
                      <p className="text-gray-600">{selectedExample.description}</p>
                    </div>
                    <span
                      className={`px-3 py-1 rounded ${
                        frameworkColors[selectedExample.framework]
                      }`}
                    >
                      {selectedExample.framework}
                    </span>
                  </div>

                  <div className="flex gap-3">
                    <a
                      href={`https://stackblitz.com/edit/${selectedExample.stackblitzId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary flex items-center space-x-2"
                    >
                      <Play className="w-4 h-4" />
                      <span>Open in StackBlitz</span>
                      <ExternalLink className="w-4 h-4" />
                    </a>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(selectedExample.code);
                      }}
                      className="btn btn-secondary flex items-center space-x-2"
                    >
                      <Code2 className="w-4 h-4" />
                      <span>Copy Code</span>
                    </button>
                  </div>
                </div>

                {/* Code Preview */}
                <div className="card">
                  <h3 className="font-bold mb-4">Code Preview</h3>
                  <pre className="bg-gray-900 text-gray-100 p-6 rounded-lg overflow-x-auto">
                    <code className="text-sm">{selectedExample.code}</code>
                  </pre>
                </div>

                {/* StackBlitz Embed */}
                <div className="card">
                  <h3 className="font-bold mb-4">Live Demo</h3>
                  <StackBlitzEmbed projectId={selectedExample.stackblitzId} />
                </div>
              </div>
            ) : (
              <div className="card text-center py-20">
                <Code2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">
                  Select an Example
                </h3>
                <p className="text-gray-500">
                  Choose an example from the list to view the code and live demo.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Additional Resources */}
      <section className="bg-white border-t border-gray-200">
        <div className="container-custom section">
          <h2 className="text-3xl font-bold text-center mb-8">
            Want to Build Your Own?
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="card text-center">
              <Code2 className="w-12 h-12 text-salla-primary mx-auto mb-4" />
              <h3 className="font-bold mb-2">API Reference</h3>
              <p className="text-sm text-gray-600 mb-4">
                Comprehensive API documentation for all features
              </p>
              <a href="/api" className="text-salla-primary hover:underline">
                View API Docs →
              </a>
            </div>

            <div className="card text-center">
              <Code2 className="w-12 h-12 text-salla-primary mx-auto mb-4" />
              <h3 className="font-bold mb-2">Guides</h3>
              <p className="text-sm text-gray-600 mb-4">
                Step-by-step guides for common use cases
              </p>
              <a href="/guides" className="text-salla-primary hover:underline">
                Read Guides →
              </a>
            </div>

            <div className="card text-center">
              <Code2 className="w-12 h-12 text-salla-primary mx-auto mb-4" />
              <h3 className="font-bold mb-2">Documentation</h3>
              <p className="text-sm text-gray-600 mb-4">
                Complete documentation for getting started
              </p>
              <a href="/docs" className="text-salla-primary hover:underline">
                Get Started →
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ExamplesPage;

